import React, { useEffect, useState } from 'react';

// Self-contained phone verification, entirely independent of Authentik's
// flow-executor challenges — see phoneVerification.js (backend) header
// for why this exists. `onVerified` fires once the code is confirmed and
// contact_phone has been persisted; the parent decides what to render
// next (there is no "next Authentik stage" tied to this step at all).

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const lbl = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: '#5F5E5A',
  marginBottom: '5px',
};

const inp = {
  width: '100%',
  padding: '10px 14px',
  border: '1.5px solid #D3D1C7',
  borderRadius: '8px',
  fontSize: '15px',
  boxSizing: 'border-box',
  outline: 'none',
};

const codeInp = { ...inp, textAlign: 'center', fontSize: '20px', letterSpacing: '4px' };

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };

const btnSecondary = {
  width: '100%',
  padding: '12px',
  background: 'none',
  border: '1px solid #D3D1C7',
  color: '#666',
  borderRadius: '10px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  marginTop: '10px',
};

// phone: already-collected 10-digit number (e.g. from a prior prompt) to
// send to automatically on mount. username: needed by /confirm to look
// up and persist onto the right account. onCancel: lets the person back
// out mid-verification — contact_phone is only ever written on a
// successful /confirm, so cancelling here just voids the outstanding code
// (best-effort; nothing on the account itself needs undoing). deferPersist:
// Register.jsx only — Authentik's own flow is still running at this exact
// point in registration (paused at whatever comes after the identity
// prompt), so writing contact_phone immediately here would race it (see
// phoneVerification.js's /confirm header). Self-service add-phone omits
// this — its flow has already fully completed by the time this renders.
const RESEND_COOLDOWN_SECONDS = 30;

export default function PhoneVerifyStep({ phone, username, onVerified, onCancel, deferPersist, dark }) {
  const labelStyle = dark ? { ...lbl, color: 'rgba(255, 255, 255, 0.85)' } : lbl;
  const bodyColor = dark ? 'rgba(255, 255, 255, 0.75)' : '#666';
  const secondaryBtnStyle = dark
    ? { ...btnSecondary, border: '1px solid rgba(255, 255, 255, 0.35)', color: 'rgba(255, 255, 255, 0.75)' }
    : btnSecondary;
  const [verificationId, setVerificationId] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  // Separate from `loading` — a resend happening in the background
  // shouldn't disable/hide the code form the person may still be looking
  // at from the PREVIOUS code.
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Counts a resend cooldown down to 0 once started — cheap client-side
  // throttle so someone can't hammer the SMS provider (real cost per
  // send) by mashing Resend; the OTP's own 5-minute server-side TTL is
  // the actual security boundary, this is just UX/cost hygiene.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  async function sendCode({ isResend } = {}) {
    if (isResend) {
      setResending(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/phone-verify/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, username, resend: !!isResend }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not send verification code.');
        return;
      }
      setVerificationId(data.verificationId);
      // data.sent === false means an already-live code was reused (e.g. this
      // mount was just a page refresh, not a real send) — don't restart the
      // cooldown for a send that didn't actually happen.
      if (data.sent !== false) {
        setCode('');
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      }
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
      setResending(false);
    }
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      if (verificationId) {
        await fetch(`${API_BASE}/phone-verify/cancel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ verificationId }),
        });
      }
    } catch (e) {
      // Best-effort — the code expires on its own in 5 minutes either way.
    } finally {
      onCancel();
    }
  }

  async function submitCode(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/phone-verify/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verificationId, code, username, defer: !!deferPersist }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Incorrect code. Please try again.');
        return;
      }
      // data.emailPendingSent: registration-only (deferPersist) — true if
      // an email was ALSO given and its confirmation was just sent now
      // that phone verification succeeded (see phoneVerification.js).
      // Always undefined for self-service add-phone; harmless there.
      onVerified(data.phone, data.emailPendingSent);
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <p style={{ textAlign: 'center', color: bodyColor, fontSize: '14px', marginBottom: '20px' }}>
        {verificationId
          ? "We've sent a verification code to your phone."
          : 'Sending verification code…'}
      </p>

      {error && (
        <div style={{
          background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
          borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      {verificationId && (
        <form onSubmit={submitCode}>
          <label style={labelStyle}>Enter the 6-digit code *</label>
          <input
            style={codeInp}
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
            required
          />
          <button type="submit" disabled={loading || code.length !== 6} style={(loading || code.length !== 6) ? btnDisabled : btn}>
            {loading ? 'Verifying…' : 'Verify'}
          </button>
          <button
            type="button"
            onClick={() => sendCode({ isResend: true })}
            disabled={resending || resendCooldown > 0}
            style={{
              ...secondaryBtnStyle,
              opacity: (resending || resendCooldown > 0) ? 0.6 : 1,
              cursor: (resending || resendCooldown > 0) ? 'not-allowed' : 'pointer',
            }}
          >
            {resending ? 'Resending…' : resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend Code'}
          </button>
        </form>
      )}

      {!verificationId && !loading && (
        <button onClick={() => sendCode()} style={btn}>
          Try Again
        </button>
      )}

      {onCancel && (
        <button onClick={handleCancel} disabled={cancelling} style={secondaryBtnStyle}>
          {cancelling ? 'Cancelling…' : 'Cancel'}
        </button>
      )}
    </div>
  );
}
